import React, { useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { colors } from "app/popleads/components/ui-kit/colors";
import EmptyStatus from "app/popleads/components/ui-kit/EmptyStatus";
import Spinner from "app/popleads/components/ui-kit/Spinner";
import Button from "app/popleads/components/ui-kit/Button";
import { StyledCardHeader } from "./common/styled";
import useGetCainNotifications from "app/popleads/hooks/useGetCainNotifications";

const ROWS_PER_PAGE = 20;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    height: 100%;
`;

const ListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const NotificationCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    background-color: rgb(250, 250, 250);
    border-radius: 6px;
    border: 1px solid ${colors.neutral[100]};
    box-shadow: 0px 0px 1px 0px rgba(0, 0, 0, 0.2);
    padding: 8px;
    cursor: pointer;

    &:hover {
        border-color: ${colors.l3rnTech[200]};
    }
`;

const NotificationTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const NotificationTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.17px;
    color: ${colors.neutral[900]};
`;

const NotificationCourse = styled.div`
    font-size: 12px;
    font-weight: 500;
    color: ${colors.neutral[600]};
    overflow-wrap: anywhere;
`;

const NotificationFooter = styled.div`
    display: flex;
    align-items: center;
`;

const StyledLink = styled.a`
    color: ${colors.l3rnTech[900]};
    text-decoration: none;
    font-weight: 600;
    &:hover {
        color: inherit;
    }
`;

const LoadMoreContainer = styled.div`
    display: flex;
    justify-content: center;
    padding-top: 4px;
`;

function ExtensionCainNotifications() {
    const [page, setPage] = useState(1);
    const [notifications, setNotifications] = useState([]);

    const { data, loading, error } = useGetCainNotifications({ isRead: 1, page, rowsPerPage: ROWS_PER_PAGE });

    useEffect(() => {
        if (!data) return;
        setNotifications((prev) => {
            const merged = page === 1 ? data.notifications ?? [] : [...prev, ...(data.notifications ?? [])];
            return [...merged].sort((a, b) => b.createdAt - a.createdAt);
        });
    }, [data]);

    const handleNotificationClick = (notification) => {
        if (notification.linkedinUrl) {
            window.open(notification.linkedinUrl, "_blank", "noopener,noreferrer");
        }
    };

    if (loading && page === 1) {
        return (
            <LoadingContainer>
                <Spinner size={32} thickness={4} variant="l3rnTech" />
            </LoadingContainer>
        );
    }

    if (error && notifications.length === 0) {
        return <EmptyStatus description="Notifications could not be loaded." />;
    }

    if (notifications.length === 0) {
        return <EmptyStatus description="No new notifications." />;
    }

    return (
        <ListContainer>
            {notifications.map((notification) => (
                <NotificationCard key={notification.id} onClick={() => handleNotificationClick(notification)}>
                    <StyledCardHeader>
                        {moment(notification.createdAt).format("ddd, MMM DD, YYYY HH:mm")}
                    </StyledCardHeader>
                    <NotificationTitleRow>
                        <NotificationTitle>{notification.title}</NotificationTitle>
                    </NotificationTitleRow>
                    {notification.linkedinContact?.courseName && (
                        <NotificationCourse>
                            Recommended Course: {notification.linkedinContact.courseName}
                        </NotificationCourse>
                    )}
                    {notification.linkedinUrl && (
                        <NotificationFooter>
                            <StyledLink
                                href={notification.linkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(event) => event.stopPropagation()}
                            >
                                {notification.linkedinName}
                            </StyledLink>
                        </NotificationFooter>
                    )}
                </NotificationCard>
            ))}
            {data?.hasNextPage && (
                <LoadMoreContainer>
                    <Button
                        size="small"
                        buttonStyle="option"
                        variant="l3rnTech"
                        loading={loading && page > 1}
                        onClick={() => setPage((prev) => prev + 1)}
                    >
                        Load more
                    </Button>
                </LoadMoreContainer>
            )}
        </ListContainer>
    );
}

export default ExtensionCainNotifications;
